import { ChangeDetectionStrategy, Component, HostListener, Signal, computed, effect, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Observable, map } from 'rxjs';

import { toSignal } from '@angular/core/rxjs-interop';
import {
    ActionItem,
    ApiRecordType,
    BaseContainer,
    DialogConfig,
    DialogRef,
    ErrorResponse,
    FacialRecognitionConsent,
    FacialRecognitionGuard,
    FormRadioListItem,
    RecordType,
    RecordTypeInformationPackage,
    RecordTypeStructure,
    RecordTypesService,
    getRecordTypeOptionLabel
} from '@stylehaven/components';
import {
    SelectRecordTypeDialogOutput,
    SelectRecordTypeDialogOutputCancel,
    SelectRecordTypeDialogOutputSuccess
} from '../../../entities/dialog/select-record-type-dialog.entity';
@Component({
    standalone: false,
    selector: 'mhv-select-record-type-dialog',
    templateUrl: './select-record-type-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectRecordTypeDialogComponent extends BaseContainer {
    public recordTypeOptions: FormRadioListItem[];
    public dialogActionItems$: Observable<ActionItem[]>;
    public disableDropdown$: Observable<boolean>;

    public recordTypeFormGroup: UntypedFormGroup;

    private recordTypeContext: RecordType | undefined;
    private allowedStructures?: RecordTypeStructure[];
    private recordTypes: ApiRecordType[] = [];

    private $facialRecognitionActive: Signal<boolean | undefined>;
    private $isDataTypeSelected: Signal<boolean | undefined>;
    public $showFacialRecognitionConsent: Signal<boolean>;

    protected readonly ErrorResponse = ErrorResponse;

    private readonly facialRecognitionGuard: FacialRecognitionGuard = inject(FacialRecognitionGuard);

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<SelectRecordTypeDialogOutput>,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly recordTypeService: RecordTypesService
    ) {
        super();

        this.recordTypeContext = this.config.data.recordTypeContext;
        this.allowedStructures = this.config.data.allowedStructures ?? undefined;

        this.initForm();
        this.initRecordTypes();

        this.$facialRecognitionActive = toSignal(this.facialRecognitionGuard.hasAccess());

        this.$isDataTypeSelected = toSignal(
            this.recordTypeControl.valueChanges.pipe(map(recordTypeName => this.isDataType(recordTypeName))),
            { initialValue: false }
        );

        this.$showFacialRecognitionConsent = computed(
            () => this.$facialRecognitionActive() === true && this.$isDataTypeSelected() === true
        );

        effect(() => {
            if (this.$facialRecognitionActive() !== undefined) {
                this.initAutoConfirm();
            }
        });
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        // Prevent submitting when form is invalid
        if (!this.recordTypeFormGroup.valid) {
            return;
        }
        event.preventDefault();
        this.submit();
    }

    private initForm(): void {
        this.recordTypeFormGroup = this.formBuilder.group({
            recordType: [null, Validators.required],
            facialRecognitionConsent: [null]
        });
    }

    private initRecordTypes(): void {
        if (this.recordTypeContext) {
            this.recordTypes = this.recordTypeService.getAllowedChildren(this.recordTypeContext);
        } else {
            this.recordTypes = this.recordTypeService.getActiveRecordTypes();
        }

        if (this.allowedStructures) {
            this.recordTypes = this.recordTypes.filter(recordType =>
                this.allowedStructures?.includes(recordType.Structure)
            );
        }

        const locale = this.translateService.getCurrentLocale();

        this.recordTypeOptions = this.recordTypes.map(recordType => ({
            id: recordType.Name,
            value: recordType.Name,
            label: getRecordTypeOptionLabel(recordType, locale, this.recordTypes)
        }));
    }

    private initAutoConfirm(): void {
        const facialRecognitionActive = this.$facialRecognitionActive();

        if (this.recordTypes.length > 1) {
            return;
        }

        this.recordTypeControl.setValue(this.recordTypes[0].Name);

        // Autoconfirm if no facial recognition and single record-type
        if (!facialRecognitionActive) {
            this.submit();
            return;
        }

        const showFacialRecognitionConsent = this.$showFacialRecognitionConsent();

        // Autoconfirm if facial recognition and no consent needed
        if (!showFacialRecognitionConsent) {
            this.submit();
            return;
        }
    }

    submit(): void {
        const recordTypeName: string = this.recordTypeControl.value;
        const facialRecognitionConsent: FacialRecognitionConsent | undefined = !this.$showFacialRecognitionConsent()
            ? undefined
            : this.facialRecognitionConsentControl.value
              ? FacialRecognitionConsent.Allowed
              : FacialRecognitionConsent.Disallowed;

        const output: SelectRecordTypeDialogOutputSuccess = {
            type: 'success',
            recordType: this.getRecordTypeDetails(recordTypeName)!,
            facialRecognitionConsent
        };

        this.dialog.close(output);
    }

    cancel(): void {
        const output: SelectRecordTypeDialogOutputCancel = {
            type: 'cancel'
        };
        this.dialog.close(output);
    }

    // #region Getters
    get recordTypeControl(): UntypedFormControl {
        return this.recordTypeFormGroup.get('recordType') as UntypedFormControl;
    }

    get facialRecognitionConsentControl(): UntypedFormControl {
        return this.recordTypeFormGroup.get('facialRecognitionConsent') as UntypedFormControl;
    }
    // #endregion

    // #region Utils
    private getRecordTypeDetails(recordTypeName: string): ApiRecordType | undefined {
        return this.recordTypes.find(recordType => recordType.Name === recordTypeName);
    }

    private isDataType(recordTypeName: string): boolean {
        const recordType = this.getRecordTypeDetails(recordTypeName);

        return (
            recordType?.Structure === RecordTypeStructure.Data &&
            recordType?.InformationPackage === RecordTypeInformationPackage.Aip
        );
    }
}
